import { createSlice } from '@reduxjs/toolkit'
import { RECENT_COLOR_LIMIT } from 'apps/studio/lib/utils/color'
import {
  addPdf,
  resetPdf,
  replacePdf,
} from 'apps/studio/store/actions/sharedActions'
import { loadFormData } from 'apps/formsCreationKit/store/actions/sharedActions'
import {
  initializePdfPages,
  backFillPagesAndLayouts,
} from 'apps/studio/store/actions/sharedActions'
import { appSettingsActions, saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'
const { updateFlashMessage } = appSettingsActions

export const DEFAULT_ZOOM = 0.5
export const MIN_ZOOM = 0.25
export const MAX_ZOOM = 5
export const LEFT_PANELS = ['Add', 'Form Outline', 'PDF Outline']
export const INTRO_MODE = 'Intro'
export const PDF_MODE = 'PDF'
export const TEST_MODE = 'Test'

const aiProgressInitialState = {
  active: false,
  completedPages: 0,
  totalPages: 0,
}

const resetAILoadingState = state => {
  return {
    ...state,
    aiProcessingProgress: aiProgressInitialState,
  }
}

const exitTestMode = state =>
  state.activeMode === TEST_MODE ? { ...state, activeMode: PDF_MODE } : state

export const initialState = {
  activeMode: INTRO_MODE,
  activeLeftPanels: LEFT_PANELS,
  zoom: DEFAULT_ZOOM,
  downloadingTestPdf: false,
  aiProcessingProgress: aiProgressInitialState,
  plgDemo: false,
  shouldInitializePages: false,
  replacingPdf: false,
  tableWarningDisplayed: false,
}

const studioAppSettingsConfig = {
  name: 'studioSettings',
  initialState,
  reducers: {
    setActiveMode: (state, action) => {
      const { activeMode } = action.payload
      return {
        ...state,
        activeMode,
      }
    },
    toggleLeftPanel: (state, action) => {
      const { panel } = action.payload
      if (state.activeLeftPanels.includes(panel)) {
        return {
          ...state,
          activeLeftPanels: state.activeLeftPanels.filter(
            name => name !== panel
          ),
        }
      } else {
        return {
          ...state,
          activeLeftPanels: [...state.activeLeftPanels, panel].sort(),
        }
      }
    },
    setZoom: (state, action) => {
      const { zoom } = action.payload
      return {
        ...state,
        zoom,
      }
    },
    setDownloadingTestPdf: (state, action) => {
      const { downloadingTestPdf } = action.payload
      return {
        ...state,
        downloadingTestPdf,
      }
    },
    setAILoadingState: (state, action) => {
      return {
        ...state,
        aiProcessingProgress: {
          ...state.aiProcessingProgress,
          ...action.payload,
        },
      }
    },
    resetAILoadingState,
    addColors: (state, action) => {
      const { colors = [] } = action.payload
      return {
        ...state,
        colors: [...colors, ...state.colors].slice(0, RECENT_COLOR_LIMIT), // keep only the first 14 colors
      }
    },
    setTableWarningDisplayed: (state, action) => {
      const { tableWarningDisplayed = true } = action.payload
      return {
        ...state,
        tableWarningDisplayed,
      }
    },
  },
  extraReducers: studio => {
    studio.addCase(loadFormData, resetAILoadingState)
    studio.addCase(updateFlashMessage, resetAILoadingState)
    studio.addCase(addPdf, (state, action) => {
      const { fileName, isAi } = action.payload
      return {
        ...state,
        activeMode: PDF_MODE,
        fileName,
        aiProcessingProgress: {
          ...state.aiProcessingProgress,
          active: isAi,
        },
        shouldInitializePages: true,
      }
    })
    studio.addCase(resetPdf, resetAILoadingState)
    studio.addCase(replacePdf, (state, action) => {
      const { fileName, addedPages } = action.payload
      return {
        ...state,
        fileName,
        replacingPdf: addedPages, // We only need this to be true when the new PDF has more pages than the old one, which is needed later when the backFillPagesAndLayouts action is dispatched
      }
    })
    studio.addCase(initializePdfPages, (state, action) => {
      return {
        ...state,
        shouldInitializePages: false,
      }
    })
    studio.addCase(backFillPagesAndLayouts, (state, action) => {
      return {
        ...state,
        replacingPdf: false,
      }
    })
    studio.addCase(saveApp.pending, exitTestMode)
    studio.addCase(saveApp.fulfilled, exitTestMode)
    studio.addCase(saveApp.rejected, exitTestMode)
  },
}

const studioSettingsSlice = createSlice(studioAppSettingsConfig)
export const {
  actions: studioSettingsActions,
  reducer: studioSettingsReducer,
} = studioSettingsSlice
